import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const C = {
  panel: "#050806",
  card: "#080f1a",
  border: "#f7c94855",
  gold: "#ffd84a",
  dim: "#9f7a24",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
};

function num(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function fmt(value) {
  return num(value).toLocaleString("en-IN");
}

function fmtSpot(value) {
  return num(value) ? num(value).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "-";
}

function fmtCr(value) {
  const next = num(value);
  if (!next) return "0";
  return `${(next / 10000000).toFixed(2)} Cr`;
}

function fmtSignedCr(value) {
  const next = num(value);
  if (!next) return "0";
  return `${next > 0 ? "+" : ""}${(next / 10000000).toFixed(2)} Cr`;
}

function fmtTime(value) {
  return value ? new Date(value).toLocaleTimeString("en-IN", { hour12: false }) : "";
}

async function getJson(url) {
  const res = await fetch(url, { cache: "no-store" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || "Writer Matrix history fetch failed");
  }
  return data;
}

function SelectBox({ label, value, onChange, children, disabled }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 9, letterSpacing: 3, color: C.dim, marginBottom: 5 }}>{label}</div>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        style={{
          minWidth: 220,
          background: "#020202",
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          color: C.gold,
          fontFamily: "inherit",
          fontSize: 12,
          fontWeight: "bold",
          letterSpacing: 1,
          padding: "9px 12px",
        }}
      >
        {children}
      </select>
    </label>
  );
}

function Stat({ label, value, color = C.gold }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, background: "#020202" }}>
      <div style={{ fontSize: 8, letterSpacing: 1.4, color: C.dim, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

function MatrixIndexPanel({ item }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, background: C.card }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.dim }}>{item.index} LIVE SPOT</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.gold, marginTop: 6 }}>{fmtSpot(item.liveSpot)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.dim }}>EXPIRY</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: C.blue, marginTop: 8 }}>{item.expiry || "-"}</div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {(item.metrics || []).map(metric => (
          <div key={`${item.indexKey}-${metric.spot}`} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: "#04090f" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 9, letterSpacing: 2.4, color: C.dim }}>SPOT LEVEL</div>
                <div style={{ fontSize: 21, fontWeight: 900, color: C.gold }}>{fmt(metric.spot)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, letterSpacing: 2.4, color: C.dim }}>ATM</div>
                <div style={{ fontSize: 17, fontWeight: 900, color: C.blue }}>{fmt(metric.atm)}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              <Stat label="TOTAL VOLUME CE - TOP 10 ITM" value={fmtCr(metric.ceTopItmVolume)} color={C.green} />
              <Stat label="TOTAL VOLUME PE - TOP 10 ITM" value={fmtCr(metric.peTopItmVolume)} color={C.red} />
              <Stat label="TOTAL CALL SELLERS" value={fmtCr(metric.callSellerOi)} color={C.red} />
              <Stat label="CALL SELLERS +/- ADDED/LEFT" value={fmtSignedCr(metric.callSellerChange)} color={num(metric.callSellerChange) >= 0 ? C.green : C.red} />
              <Stat label="TOTAL PUT SELLERS" value={fmtCr(metric.putSellerOi)} color={C.green} />
              <Stat label="PUT SELLERS +/- ADDED/LEFT" value={fmtSignedCr(metric.putSellerChange)} color={num(metric.putSellerChange) >= 0 ? C.green : C.red} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SecretExpiryWriterMatrixHistory() {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedSnapshot = useMemo(
    () => snapshots.find(item => item.id === selectedId) || snapshots[snapshots.length - 1] || null,
    [snapshots, selectedId]
  );

  const loadDates = useCallback(async () => {
    const data = await getJson(`${API_BASE}/api/secret-expiry-history/writer-matrix/dates?t=${Date.now()}`);
    setDates(data.dates || []);
    setSelectedDate(prev => prev || data.dates?.[0] || "");
  }, []);

  const loadSnapshots = useCallback(async date => {
    if (!date) {
      setSnapshots([]);
      setSelectedId("");
      return;
    }

    const data = await getJson(`${API_BASE}/api/secret-expiry-history/writer-matrix/${date}?t=${Date.now()}`);
    const rows = data.snapshots || [];
    setSnapshots(rows);
    setSelectedId(prev => rows.some(row => row.id === prev) ? prev : rows[rows.length - 1]?.id || "");
  }, []);

  useEffect(() => {
    setLoading(true);
    loadDates()
      .catch(err => setError(err.message || "Writer Matrix history dates failed"))
      .finally(() => setLoading(false));
  }, [loadDates]);

  useEffect(() => {
    setLoading(true);
    loadSnapshots(selectedDate)
      .catch(err => setError(err.message || "Writer Matrix history snapshots failed"))
      .finally(() => setLoading(false));
  }, [selectedDate, loadSnapshots]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadDates().catch(() => {});
      if (selectedDate) loadSnapshots(selectedDate).catch(() => {});
    }, 30000);

    return () => clearInterval(timer);
  }, [loadDates, loadSnapshots, selectedDate]);

  return (
    <div style={{ color: C.gold }}>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, background: C.panel, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <SelectBox label="DATE" value={selectedDate} onChange={setSelectedDate} disabled={loading || !dates.length}>
            {!dates.length && <option value="">NO DATA</option>}
            {dates.map(date => <option key={date} value={date}>{date}</option>)}
          </SelectBox>

          <SelectBox label="5 MIN DATA" value={selectedId} onChange={setSelectedId} disabled={loading || !snapshots.length}>
            {!snapshots.length && <option value="">NO SNAPSHOTS</option>}
            {snapshots.map(item => (
              <option key={item.id} value={item.id}>
                {item.intervalLabel || item.time?.slice(0, 5) || fmtTime(item.capturedAt)}
              </option>
            ))}
          </SelectBox>

          <div style={{ background: "#020202", border: `1px solid ${C.border}`, color: C.green, padding: "10px 18px", borderRadius: 4, letterSpacing: 2, fontWeight: 900, fontSize: 11 }}>
            AUTO SAVE 5 MIN
          </div>

          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            {selectedSnapshot && (
              <>
                <div style={{ fontSize: 9, letterSpacing: 3, color: C.dim }}>WRITER MATRIX SNAPSHOT</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: C.gold }}>{selectedSnapshot.intervalLabel || fmtTime(selectedSnapshot.capturedAt)}</div>
              </>
            )}
            {loading && <div style={{ color: C.blue, fontSize: 11, letterSpacing: 2 }}>LOADING HISTORY...</div>}
            {error && <div style={{ color: C.red, fontSize: 11 }}>{error}</div>}
          </div>
        </div>
      </div>

      {!selectedSnapshot && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim, letterSpacing: 4, fontSize: 11 }}>
          WAITING FOR WRITER MATRIX 5 MIN HISTORY
        </div>
      )}

      {selectedSnapshot && (
        <div style={{ display: "grid", gap: 16 }}>
          {(selectedSnapshot.indices || []).map(item => (
            <MatrixIndexPanel key={item.indexKey} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
